/**
 * File Name          : egg-drop.component.ts
 * Author             : Jared Laine Dyreson
 * Version            : 0.1.0
 * Date               : 2026-03-18
 * Description        : Short Description (manual edit required)
 * 
 * Copyright (C) Innovative Design Labs, Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * This file is subject to the terms and conditions defined in
 * file 'IDL_PROJECTTECH_LICENSE.txt', which is part of this source code package.
 */

import { Component, Input, ViewChild, ChangeDetectorRef, OnInit, OnDestroy, NgZone } from '@angular/core';
import { ImpactResults } from '../../../lesson-utils/impact-analysis';
import { CommonModule } from '@angular/common';
import { DxButtonGroupModule, DxButtonModule, DxChartModule, DxPopupComponent, DxChartComponent } from 'devextreme-angular';
import { variableSigFigsFormatter } from '../../../utils/formatters';
import { OnChanges, SimpleChanges } from '@angular/core';
import { ImpactFields } from '../../../models/lessons.model';
import { ScreenserviceService } from '../../../services/screenservice.service';
import { Subscription } from 'rxjs';

const AVAILABLE_SERIES = [{text: 'Acceleration (G)', valueField: 'acceleration', type:'default'}, {text: 'Velocity (m/s)', valueField: 'velocity', type:'default'}, {text: 'Distance (m)', valueField: 'position', type:'default'}]

@Component({
  selector: 'app-egg-drop',
  standalone: true,
  imports: [CommonModule, DxChartModule, DxButtonGroupModule, DxButtonModule, DxPopupComponent],
  templateUrl: './egg-drop.component.html',
  styleUrl: './egg-drop.component.scss'
})
export class EggDropComponent implements OnChanges, OnInit, OnDestroy {
  @Input() results: ImpactResults | undefined = undefined;
  @Input() scalars: string[] = [];
  @Input() series: string[] = [];

  @ViewChild(DxChartComponent) chart?: DxChartComponent;

  velocityMin: number | undefined = undefined;
  velocityMax: number | undefined = undefined;
  positionMin: number | undefined = undefined;
  positionMax: number | undefined = undefined;

  mobileChartSelection: string|null = null;
  chartKey: number = 0; // Used to force chart re-render on orientation change

  // Local, imperatively-managed landscape gate for the popup template.
  // The popup content is rendered via DevExtreme's portal, and binding the
  // @if in there directly to screenservice.isLandscape was unreliable on
  // the Pixel 4a repro (RWAI-19701): the getter was evaluated once when
  // the popup opened and its return value never fully propagated back
  // into the projected template even after rotation, leaving "Device
  // must be in Landscape" showing while the device was clearly landscape.
  // Setting this field imperatively and calling detectChanges at every
  // relevant moment forces the projected @if to re-evaluate against a
  // stable, freshly-computed value.
  isLandscapeView: boolean = true;

  private orientationSub?: Subscription;
  private windowOrientationHandler = () => this.refreshLandscapeGate();

  ImpactFields = ImpactFields;

  constructor(
    public screenservice: ScreenserviceService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone
  ) { }

  ngOnInit(): void {
    // Driven by the service's settled orientation signal rather than a raw
    // window resize/orientationchange listener - see ScreenserviceService for why.
    this.orientationSub = this.screenservice.orientationChanges.subscribe(() => {
      this.refreshLandscapeGate();
      this.onOrientationChange();
    });

    // Raw window listeners as a second safety net - the service can miss
    // events on some Android Chrome builds (e.g. after a Bluetooth
    // permission dialog dismisses), and the popup's @if won't re-evaluate
    // if isLandscapeView isn't updated. Listen directly so the local field
    // updates regardless of whether the service's chain fires.
    window.addEventListener('resize', this.windowOrientationHandler);
    window.addEventListener('orientationchange', this.windowOrientationHandler);

    this.refreshLandscapeGate();
  }

  ngOnDestroy(): void {
    this.orientationSub?.unsubscribe();
    window.removeEventListener('resize', this.windowOrientationHandler);
    window.removeEventListener('orientationchange', this.windowOrientationHandler);
  }

  // OR-combine multiple orientation signals - any one saying landscape is
  // enough. window.innerWidth/innerHeight is the layout viewport,
  // matchMedia is the CSS-level orientation, screen.orientation is the
  // hardware-level orientation. On the Pixel 4a repro any single signal
  // could lag stale (matchMedia particularly after a BLE permission
  // dialog), but at least one has consistently reflected reality at any
  // given moment. Taking the union avoids blocking the chart when a
  // single signal is temporarily wrong.
  private computeLandscape(): boolean {
    const byDimensions = window.innerWidth > window.innerHeight;
    const byMatchMedia = window.matchMedia?.('(orientation: landscape)')?.matches ?? false;
    const orientationType = (screen as any)?.orientation?.type as string | undefined;
    const byScreenApi = typeof orientationType === 'string' && orientationType.startsWith('landscape');
    return byDimensions || byMatchMedia || byScreenApi;
  }

  private refreshLandscapeGate(): void {
    this.zone.run(() => {
      const next = this.computeLandscape();
      if (next !== this.isLandscapeView) {
        this.isLandscapeView = next;
      }
      this.cdr.detectChanges();
    });
  }

  onOrientationChange(): void {
    // Force chart re-render on orientation change
    this.chartKey++;
    this.cdr.detectChanges();

    // Give the DOM time to update, then force a full remeasure - without
    // { force: true } DevExtreme can skip re-measuring the container after
    // rotation, leaving the chart sized to a stale (pre-rotation) reading,
    // which is what produced the cramped/truncated layout on rotation.
    setTimeout(() => {
      if (this.chart?.instance) {
        this.chart.instance.render({ force: true });
      }
    }, 100);
  }

  // The mobile chart popup can be opened without any intervening rotation
  // event (device already in its current orientation) - the service's own
  // listeners never fire in that case, so isLandscape could still reflect a
  // stale read from before the page settled. Force a fresh check right as
  // the popup opens instead of relying solely on passive orientation events.
  openMobileChart(seriesValueField: string): void {
    this.screenservice.recheckOrientation();
    this.refreshLandscapeGate();
    this.mobileChartSelection = seriesValueField;
    this.onOrientationChange();
    // The popup mounts a moment after the click - re-check on a short
    // delay too so the projected @if evaluates against the popup's
    // post-mount viewport, not the click-time viewport.
    setTimeout(() => this.refreshLandscapeGate(), 50);
    setTimeout(() => this.refreshLandscapeGate(), 300);
  }

  // DevExtreme mounts the popup's content on its own show/animation lifecycle,
  // not synchronously with the click that sets mobileChartSelection - relying
  // solely on that click-time check left a window where the popup could still
  // render against a stale orientation read. onShowing re-validates right
  // before content mounts; onShown re-measures once the popup has finished
  // animating in and the chart has its final on-screen size.
  onPopupShowing(): void {
    this.screenservice.recheckOrientation();
    this.refreshLandscapeGate();
  }

  onPopupShown(): void {
    this.refreshLandscapeGate();
    this.onOrientationChange();
    setTimeout(() => this.refreshLandscapeGate(), 200);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['results']) {
      this.updateResultExtrema();
    }

    if (changes['scalars']) {
      // console.log('scalars changed:', changes['scalars'].currentValue);
    }
    if (changes['series']) {
      // console.log('series changed:', changes['series'].currentValue);
      this.availableDataSeries = AVAILABLE_SERIES.filter((s: any) => (changes['series'].currentValue as string[]).includes(s.valueField))
      this.selectedDataSeries = []
    }
  }

  private updateResultExtrema(): void {
    const data = this.results?.data ?? [];

    if (data.length === 0) {
      this.velocityMin = undefined;
      this.velocityMax = undefined;
      this.positionMin = undefined;
      this.positionMax = undefined;
      return;
    }

    this.velocityMin = Math.min(...data.map((point) => point.velocity));
    this.velocityMax = Math.max(...data.map((point) => point.velocity));
    const velocityRange = this.velocityMax - this.velocityMin;
    if (velocityRange === 0) {
      // If all velocity values are the same, set a default range around that value
      this.velocityMin -= 1; // add 50% padding below for better chart display
      this.velocityMax += 1; // add 50% padding above for better chart display
    } else {
      this.velocityMin -= Math.abs((velocityRange) * 0.2); // add 20% padding below min for better chart display
      this.velocityMax += Math.abs((velocityRange) * 0.2); // add 20% padding above max for better chart display
    }

    this.positionMin = Math.min(...data.map((point) => point.position));
    this.positionMax = Math.max(...data.map((point) => point.position));
    const positionRange = this.positionMax! - this.positionMin!;
    if (positionRange === 0) {
      // If all position values are the same, set a default range around that value
      this.positionMin -= 1; // add 50% padding below for better chart display
      this.positionMax += 1;
    } else {  
      this.positionMin -= Math.abs(positionRange * 0.2); // add 20% padding below min for better chart display
      this.positionMax += Math.abs(positionRange * 0.2); // add 20% padding above max for better chart display
    }
    

  }


  localVariableSigFigsFormatter = variableSigFigsFormatter;

  availableDataSeries = AVAILABLE_SERIES as any;
  selectedDataSeries: string[] = [];



  isSeriesSelected(series: string): boolean {
    return this.selectedDataSeries.includes(series);
  }
  customizeTooltip = ({ argumentText, valueText }: Record<string, string>) => {
    return {
      text: `${Number(argumentText).toFixed(2)}s, ${Number(valueText).toFixed(2)}${this.getUnits()}`,
    };
  }

  getUnits = () => {
    if(this.isSeriesSelected('acceleration')) return "G"
    if(this.isSeriesSelected('velocity')) return "m/s"
    if(this.isSeriesSelected('position')) return "m"
    return ""
  }

  roundDownChartLabel = (argument: { value: string | number | Date; valueText: string; }) => {
    const value = argument.value as number;
    return (value >= 0 ? Math.floor(value) : Math.ceil(value)).toString();
  }

  getChartPopupTitle = () => {
    return AVAILABLE_SERIES.find(s => s.valueField === this.mobileChartSelection)?.text ?? "Data Chart";
  }

  // Zone labels are rendered as HTML spans overlaid on the chart plot area
  // rather than as DevExtreme strip labels - DX centers a strip label on its
  // midpoint and clips it at the plot edges, dropping labels for narrow
  // zones pinned to the domain edge (Held, at t=0). We position our own
  // spans from the argument axis's visual range and pixel extent, corrected
  // for the SVG's DOM offset. Wired to both onDrawn and onZoomEnd so it runs
  // on initial render, every DX redraw, and every zoom/pan gesture.
  //
  // Not using the SVG strip <rect>'s getBoundingClientRect for positioning:
  // DX draws each strip as a wide rect and clips it visually with a
  // clip-path, so the rect's own bounds extend well past the plot area and
  // are useless as a positioning source.
  onChartDrawn = (e: any) => {
    requestAnimationFrame(() => this.repositionZoneLabels(e));
  }

  // On zoom, DX recomputes the value axis's left margin from the new tick
  // labels' text metrics, but that first pass sometimes uses stale sizing
  // and under-allocates space for the rotated axis title, clipping it
  // against the container's left edge. A forced second render (same pattern
  // used for orientation change above) makes DX re-measure and re-layout
  // against the current tick labels, which corrects the margin.
  onChartZoomEnd = (e: any) => {
    requestAnimationFrame(() => this.repositionZoneLabels(e));
    setTimeout(() => {
      e.component?.render?.({ force: true });
    }, 50);
  }

  private repositionZoneLabels(e: any): void {
    const timeline = this.results?.timeline;
    if (!timeline) return;

    const chartEl: HTMLElement | undefined = e.element instanceof HTMLElement
      ? e.element
      : (typeof e.component?.element === 'function' ? e.component.element() : undefined);
    if (!chartEl) return;

    const wrapper = chartEl.closest('.chart-with-labels') as HTMLElement | null;
    if (!wrapper) return;
    const overlay = wrapper.querySelector<HTMLElement>(':scope > .zone-label-overlay');
    if (!overlay) return;

    const svg = chartEl.querySelector('svg');
    if (!svg) {
      this.hideAllZoneLabels(overlay);
      return;
    }

    const argAxis = e.component.getArgumentAxis?.();
    if (!argAxis) {
      this.hideAllZoneLabels(overlay);
      return;
    }

    const vr = argAxis.visualRange?.();
    if (!vr || vr.startValue == null || vr.endValue == null || vr.startValue >= vr.endValue) {
      this.hideAllZoneLabels(overlay);
      return;
    }

    // Axis pixel extent in the SVG's own coordinate system. In DX 25 this
    // is [startPx, endPx]; guard for the object shape too.
    const visibleArea = argAxis.getVisibleArea?.();
    let axisStart: number | undefined;
    let axisEnd: number | undefined;
    if (Array.isArray(visibleArea) && visibleArea.length >= 2) {
      axisStart = visibleArea[0];
      axisEnd = visibleArea[1];
    } else if (visibleArea && typeof visibleArea === 'object') {
      axisStart = (visibleArea as any).start;
      axisEnd = (visibleArea as any).end;
    }
    if (axisStart == null || axisEnd == null || axisEnd <= axisStart) {
      this.hideAllZoneLabels(overlay);
      return;
    }

    // Translate SVG-space axis pixels into overlay-space pixels. The overlay
    // covers the wrapper with inset:0, so overlay origin == wrapper origin.
    const svgRect = svg.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    const svgOffsetX = svgRect.left - wrapperRect.left;
    const plotLeft = svgOffsetX + axisStart;
    const plotRight = svgOffsetX + axisEnd;
    // Zone labels sit just below the plot's actual top edge, not the
    // overlay's - the overlay spans the whole chart component, but DX
    // reserves space above the plot rectangle (e.g. for chart padding),
    // so a fixed CSS `top` sits above the strip rects instead of on them.
    const svgOffsetY = svgRect.top - wrapperRect.top;
    const labelTop = svgOffsetY + 6;
    if (plotRight <= plotLeft) {
      this.hideAllZoneLabels(overlay);
      return;
    }

    const dataToPx = (v: number) =>
      plotLeft + ((v - vr.startValue) / (vr.endValue - vr.startValue)) * (plotRight - plotLeft);

    const zones: { key: string; start: number | undefined; end: number | undefined }[] = [
      { key: 'held',     start: timeline.hold_start,     end: timeline.hold_end },
      { key: 'drop',     start: timeline.freefall_start,  end: timeline.freefall_end },
      { key: 'impact',   start: timeline.freefall_end,    end: timeline.settling_start },
      { key: 'settling', start: timeline.settling_start,  end: timeline.settling_end },
    ];

    type Placement = { el: HTMLElement; cx: number; halfW: number; visWidthPx: number; hide: boolean };
    const placements: (Placement | null)[] = zones.map(z => {
      const el = overlay.querySelector<HTMLElement>(`[data-zone="${z.key}"]`);
      if (!el) return null;
      el.style.visibility = 'hidden';

      if (z.start == null || z.end == null || z.start >= z.end) return null;

      const visStart = Math.max(z.start, vr.startValue);
      const visEnd = Math.min(z.end, vr.endValue);
      if (visStart >= visEnd) return null;

      const leftPx = dataToPx(visStart);
      const rightPx = dataToPx(visEnd);
      const visWidthPx = rightPx - leftPx;
      if (visWidthPx <= 0) return null;

      const labelW = el.offsetWidth;
      if (labelW <= 0) return null;

      // Clamp to the plot area so the label always sits over the plot,
      // not over the y-axis labels or the right-margin whitespace.
      const margin = 2;
      let cx = leftPx + visWidthPx / 2;
      cx = Math.max(plotLeft + labelW / 2 + margin, cx);
      cx = Math.min(plotRight - labelW / 2 - margin, cx);

      return { el, cx, halfW: labelW / 2, visWidthPx, hide: false };
    });

    // Overlap resolution: order by rendered x-center; if adjacent bboxes
    // touch, hide the label whose zone has the smaller visible slice.
    const visible = placements.filter((p): p is Placement => p !== null);
    visible.sort((a, b) => a.cx - b.cx);
    const collisionPad = 4;
    for (let i = 0; i < visible.length - 1; i++) {
      const a = visible[i];
      const b = visible[i + 1];
      if (a.hide || b.hide) continue;
      if (a.cx + a.halfW + collisionPad > b.cx - b.halfW) {
        (a.visWidthPx < b.visWidthPx ? a : b).hide = true;
      }
    }

    for (const p of placements) {
      if (!p) continue;
      if (p.hide) {
        p.el.style.visibility = 'hidden';
      } else {
        p.el.style.left = `${p.cx}px`;
        p.el.style.top = `${labelTop}px`;
        p.el.style.visibility = 'visible';
      }
    }
  }

  private hideAllZoneLabels(overlay: HTMLElement): void {
    overlay.querySelectorAll<HTMLElement>('.zone-label').forEach(el => {
      el.style.visibility = 'hidden';
    });
  }
}
